import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { isApiClientError, isValidationApiError } from "../api/errors";
import { permissionLabel } from "../entities/access/permissionLabels";
import { type AdminUser, type Permission, type Role } from "../entities/access";
import { shellBootstrapKey, shellBootstrapQuery } from "./bootstrap";
import type { ShellServices } from "./services";
import { Select } from "./Select";
import { Notice } from "./Notice";

type Decision = "allow" | "deny";
type UserScopeOption = { id: string; name: string; code: string };

function userScopeOptions(user: AdminUser): UserScopeOption[] {
  return user.scopes.flatMap((scope) => {
    if (
      typeof scope.id !== "string" ||
      typeof scope.name !== "string" ||
      typeof scope.code !== "string"
    ) {
      return [];
    }
    return [{ id: scope.id, name: scope.name, code: scope.code }];
  });
}

/**
 * Выбор организации или КПО, в контексте которого назначается роль или
 * прямое право. Один и тот же список используется в блоках «Назначенные роли»
 * и «Прямые права»; различаются подпись, подсказка и то, что сбрасывается при
 * смене контекста, — это остаётся за вызывающим в `onPick`.
 */
function AccessScopePicker({
  label,
  scopes,
  value,
  disabled,
  hint,
  onPick,
}: {
  label: string;
  scopes: readonly UserScopeOption[];
  value: string;
  disabled: boolean;
  hint?: string;
  onPick: (scopeId: string) => void;
}) {
  return (
    <label className="access-scope-picker">
      <span>{label}</span>
      <Select
        aria-label={label}
        value={value}
        disabled={disabled}
        onChange={(event) => onPick(event.target.value)}
      >
        <option value="">Выберите организацию или КПО…</option>
        {scopes.map((scope) => (
          <option key={scope.id} value={scope.id}>
            {scope.name}
          </option>
        ))}
      </Select>
      {hint === undefined ? null : <small>{hint}</small>}
    </label>
  );
}

function scopeId(scope: AdminUser["current_scope"]): string {
  return scope !== null && typeof scope.id === "string" ? scope.id : "";
}

function departmentOptions(
  scopeName: string,
  departments: readonly {
    id: string;
    name: string;
    parent_id: string | null;
  }[],
): Array<{ id: string; label: string }> {
  const byId = new Map(
    departments.map((department) => [department.id, department]),
  );
  const labelFor = (department: (typeof departments)[number]): string => {
    const ancestors = [department.name];
    let parentId = department.parent_id;
    while (parentId) {
      const parent = byId.get(parentId);
      if (!parent) break;
      ancestors.unshift(parent.name);
      parentId = parent.parent_id;
    }
    return [scopeName, ...ancestors].join(" -> ");
  };

  return departments
    .map((department) => ({ id: department.id, label: labelFor(department) }))
    .toSorted((left, right) => left.label.localeCompare(right.label, "ru"));
}

function errorMessage(error: unknown, holdingWide = false): string {
  if (isValidationApiError(error)) {
    const membershipError = error.details.fields.user?.[0];
    if (membershipError?.includes("active member")) {
      return "Сначала добавьте пользователя в «Состав в организации» выше: прямые права назначаются в выбранном КПО.";
    }
    const permissionError = error.details.fields.permissions?.[0];
    if (permissionError?.includes("unknown")) {
      return "Выбранное право больше недоступно в каталоге. Обновите карточку и выберите право снова.";
    }
    const fieldMessage = Object.values(error.details.fields).flat()[0];
    if (typeof fieldMessage === "string") return fieldMessage;
  }
  if (isApiClientError(error)) {
    if (error.status === 403)
      return holdingWide
        ? "Назначать доступ на весь холдинг может только системный администратор"
        : "Недостаточно прав для этой организации";
    if (error.status === 409) return "Выберите организацию";
    if (error.status === 422)
      return "Сервер отклонил изменение. Проверьте выбранные права и роли.";
  }
  return "Изменение не сохранено. Повторите попытку.";
}

const HOLDING_WIDE_CONFIRMATION =
  "Роль будет действовать во всех организациях, а пользователь увидит все организации. Это доступ уровня генерального директора или системного администратора. Продолжить?";

export function UserAccessSection({
  user,
  roles,
  permissions,
  services,
}: {
  user: AdminUser;
  roles: Role[];
  permissions: Permission[];
  services: ShellServices;
}) {
  const queryClient = useQueryClient();
  const bootstrap = useQuery(shellBootstrapQuery(services)).data;
  // Права в интерфейсе — подсказка: решает сервер (ES-87). Действие «на весь
  // холдинг» видит только системный администратор.
  const viewerIsSystemAdministrator =
    bootstrap?.user.is_system_administrator === true;
  const allOrganizationsMode = bootstrap?.allOrganizations === true;
  const holdingWideRoles = useMemo(
    () => new Set(user.holding_wide_roles ?? []),
    [user.holding_wide_roles],
  );
  const [lastHoldingWide, setLastHoldingWide] = useState(false);
  const [roleId, setRoleId] = useState("");
  const [roleDepartmentId, setRoleDepartmentId] = useState("");
  const [permissionKey, setPermissionKey] = useState("");
  const [decision, setDecision] = useState<Decision>("allow");
  const [checkedPermission, setCheckedPermission] = useState("");
  const availableScopes = useMemo(() => userScopeOptions(user), [user]);
  const currentScopeId = scopeId(user.current_scope);
  const initialScopeId = availableScopes.some(
    (scope) => scope.id === currentScopeId,
  )
    ? currentScopeId
    : "";
  const [requestedScopeId, setRequestedScopeId] = useState(initialScopeId);
  const [activeScopeId, setActiveScopeId] = useState(initialScopeId);
  const selectScope = useMutation({
    mutationFn: (scope: string) => services.administration.selectScope(scope),
    onSuccess: async (_, scope) => {
      setActiveScopeId(scope);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: shellBootstrapKey }),
        queryClient.invalidateQueries({ queryKey: ["roles"] }),
        queryClient.invalidateQueries({ queryKey: ["user", user.id] }),
      ]);
    },
  });
  const departments = useQuery({
    queryKey: ["access", "departments", activeScopeId],
    queryFn: () => services.administration.listDepartments(activeScopeId),
    enabled: Boolean(activeScopeId),
  });
  const overrides = useQuery({
    queryKey: ["access", "overrides", user.id, activeScopeId],
    queryFn: () => services.access!.overrides(user.id),
    enabled: Boolean(activeScopeId),
  });
  const effective = useQuery({
    // Подразделение бэкенд берёт из членства инспектируемого сотрудника.
    queryKey: ["access", "effective", user.id, activeScopeId],
    queryFn: () => services.access!.effectivePermissions(user.id),
    enabled: Boolean(activeScopeId),
  });
  // Системные роли здесь остаются: карточка пользователя — единственное место,
  // где их выдают (например, преемнику системного администратора).
  // Каталог ролей общий для холдинга, поэтому доступны все роли: где роль
  // будет действовать, задаёт само назначение.
  const availableRoles = roles;
  const assignedRoles = useMemo(
    () => availableRoles.filter((role) => user.roles.includes(role.slug)),
    [availableRoles, user.roles],
  );
  const unassignedRoles = useMemo(
    () => availableRoles.filter((role) => !user.roles.includes(role.slug)),
    [availableRoles, user.roles],
  );
  const departmentRoleOptions = useMemo(
    () =>
      departmentOptions(
        availableScopes.find((scope) => scope.id === activeScopeId)?.name ??
          "Организация",
        departments.data ?? [],
      ),
    [activeScopeId, availableScopes, departments.data],
  );
  const selectedPermission = permissions.find(
    (item) => item.key === checkedPermission,
  );
  const selectedAllowed = selectedPermission
    ? (effective.data?.data.permissions.includes(selectedPermission.key) ??
      false)
    : false;
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: shellBootstrapKey }),
      queryClient.invalidateQueries({ queryKey: ["users"] }),
      queryClient.invalidateQueries({ queryKey: ["user", user.id] }),
      queryClient.invalidateQueries({ queryKey: ["access"] }),
    ]);
  };
  const mutation = useMutation({
    mutationFn: ({
      operation,
    }: {
      operation: () => Promise<unknown>;
      holdingWide?: boolean;
    }) => operation(),
    onMutate: ({ holdingWide }) => setLastHoldingWide(holdingWide === true),
    onSuccess: async () => {
      await invalidate();
    },
  });
  const source = (key: string) => {
    const direct = overrides.data?.[key];
    if (direct === "deny")
      return "Прямой запрет имеет приоритет над правами ролей.";
    if (direct === "allow")
      return "Прямое разрешение добавлено отдельно от ролей.";
    const role = assignedRoles.find((item) => item.permissions.includes(key));
    return role
      ? `Право предоставлено ролью «${role.name}».`
      : "Это право не предоставлено ни одной из назначенных ролей.";
  };

  return (
    <section className="user-access" aria-labelledby="user-access-title">
      <div className="user-access__heading">
        <div>
          <p className="eyebrow">Доступ пользователя</p>
          <h3 id="user-access-title">Роли и прямые права</h3>
          <p>
            Роли задают обычный доступ. Прямые права — редкие исключения и
            всегда имеют приоритет.
          </p>
        </div>
        <div className="user-access__summary" aria-label="Сводка доступа">
          <span>
            {assignedRoles.length}{" "}
            {assignedRoles.length === 1 ? "роль" : "роли"}
          </span>
          <span>{Object.keys(overrides.data ?? {}).length} исключений</span>
        </div>
      </div>

      <div className="user-access__columns">
        <div className="access-card">
          <div className="access-card__heading">
            <div>
              <h4>Назначенные роли</h4>
              <p>
                Роли показаны для выбранной организации или КПО. Подразделение
                ограничивает роль, а без него она действует во всём контексте.
              </p>
            </div>
          </div>
          {allOrganizationsMode ? (
            <p className="access-hint" role="note">
              Выберите организацию, в которой нужно изменить доступ. Показаны
              только организации, к которым у вас есть доступ.
            </p>
          ) : null}
          <AccessScopePicker
            label="Организация или КПО для роли"
            scopes={availableScopes}
            value={requestedScopeId}
            disabled={!availableScopes.length || selectScope.isPending}
            onPick={(scope) => {
              setRequestedScopeId(scope);
              setActiveScopeId("");
              setRoleId("");
              setRoleDepartmentId("");
              if (scope) selectScope.mutate(scope);
            }}
          />
          {assignedRoles.length ? (
            <ul className="access-list">
              {assignedRoles.map((role) => {
                const holdingWide = holdingWideRoles.has(role.slug);
                // Снять назначение на весь холдинг может только системный
                // администратор — у остальных кнопки нет вовсе.
                const removable = !holdingWide || viewerIsSystemAdministrator;
                return (
                  <li key={role.id}>
                    <span className="access-list__body">
                      <strong>{role.name}</strong>
                      <small>
                        {role.is_system
                          ? "Системная роль"
                          : `${role.permissions.length} прав`}
                        {holdingWide ? " · Весь холдинг" : ""}
                      </small>
                    </span>
                    {removable ? (
                      <button
                        className="button button--text access-list__remove"
                        type="button"
                        disabled={
                          mutation.isPending || (!holdingWide && !activeScopeId)
                        }
                        onClick={() =>
                          mutation.mutate({
                            holdingWide,
                            operation: () =>
                              services.access!.revokeRole(
                                role.id,
                                user.id,
                                undefined,
                                holdingWide
                                  ? { holdingWide: true }
                                  : { scopeId: activeScopeId },
                              ),
                          })
                        }
                        aria-label={`Удалить роль ${role.name}`}
                      >
                        Удалить
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="access-empty">
              Роли не назначены. Пользователь получает доступ только через
              прямые права.
            </p>
          )}
          <div className="access-add-row">
            <label>
              <span className="visually-hidden">Роль для назначения</span>
              <Select
                aria-label="Роль для назначения"
                value={roleId}
                onChange={(event) => {
                  setRoleId(event.target.value);
                  setRoleDepartmentId("");
                }}
                disabled={
                  mutation.isPending ||
                  !activeScopeId ||
                  !unassignedRoles.length
                }
              >
                <option value="">Добавить роль…</option>
                {unassignedRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                    {role.is_system ? " · системная" : ""}
                  </option>
                ))}
              </Select>
            </label>
            <label>
              <span className="visually-hidden">Подразделение для роли</span>
              <Select
                aria-label="Подразделение для роли"
                value={roleDepartmentId}
                disabled={mutation.isPending || departments.isPending}
                onChange={(event) => setRoleDepartmentId(event.target.value)}
              >
                <option value="">Вся организация или КПО</option>
                {departmentRoleOptions.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.label}
                  </option>
                ))}
              </Select>
            </label>
            <button
              className="button button--secondary"
              type="button"
              disabled={!roleId || mutation.isPending || !activeScopeId}
              onClick={() =>
                mutation.mutate({
                  operation: async () => {
                    await services.access!.assignRole(
                      roleId,
                      user.id,
                      roleDepartmentId || null,
                      { scopeId: activeScopeId },
                    );
                    setRoleId("");
                    setRoleDepartmentId("");
                  },
                })
              }
            >
              Назначить
            </button>
          </div>
          {viewerIsSystemAdministrator ? (
            <button
              className="button button--text access-holding-wide"
              type="button"
              disabled={!roleId || mutation.isPending}
              onClick={() => {
                if (!window.confirm(HOLDING_WIDE_CONFIRMATION)) return;
                mutation.mutate({
                  holdingWide: true,
                  operation: async () => {
                    await services.access!.assignRole(
                      roleId,
                      user.id,
                      undefined,
                      { holdingWide: true },
                    );
                    setRoleId("");
                    setRoleDepartmentId("");
                  },
                });
              }}
            >
              Назначить на весь холдинг (доступ ко всем организациям)
            </button>
          ) : null}
        </div>

        <div className="access-card">
          <div className="access-card__heading">
            <div>
              <h4>Прямые права</h4>
              <p>
                Используйте только для исключений: запрет сильнее любого
                разрешения от роли.
              </p>
            </div>
          </div>
          <AccessScopePicker
            label="Организация или КПО для прямого права"
            scopes={availableScopes}
            value={requestedScopeId}
            disabled={!availableScopes.length || selectScope.isPending}
            hint="Права будут действовать только в выбранном контексте."
            onPick={(scope) => {
              setRequestedScopeId(scope);
              setActiveScopeId("");
              setPermissionKey("");
              if (scope) selectScope.mutate(scope);
            }}
          />
          {!availableScopes.length ? (
            <p className="access-empty">
              Сначала добавьте пользователя в организацию или КПО в блоке
              «Состав в организации» выше.
            </p>
          ) : null}
          {selectScope.isPending ? (
            <p className="access-empty">Переключаем контекст доступа…</p>
          ) : null}
          {selectScope.isError ? (
            <Notice>{errorMessage(selectScope.error)}</Notice>
          ) : null}
          {overrides.isLoading ? (
            <p className="access-empty">Загружаем исключения…</p>
          ) : null}
          {overrides.isError ? (
            <div className="access-load-error" role="alert">
              <p>{errorMessage(overrides.error)}</p>
              <p>
                Прямые права работают и без роли, но только для активного
                участника выбранного КПО. Пока текущие исключения не загружены,
                они не изменяются — это защищает уже назначенный доступ от
                случайной перезаписи.
              </p>
              <button
                className="button button--secondary button--compact"
                type="button"
                onClick={() => void overrides.refetch()}
              >
                Повторить загрузку
              </button>
            </div>
          ) : null}
          {Object.entries(overrides.data ?? {}).length ? (
            <ul className="access-list">
              {Object.entries(overrides.data ?? {}).map(([key, value]) => {
                const label = permissionLabel(
                  permissions.find((item) => item.key === key) ?? {
                    key,
                    group: "",
                    description: "",
                  },
                );
                return (
                  <li key={key}>
                    <span className="access-list__body">
                      <strong>{label.title}</strong>
                      <small>
                        <code>{key}</code> ·{" "}
                        <span
                          className={
                            value === "deny"
                              ? "access-state access-state--deny"
                              : "access-state access-state--allow"
                          }
                        >
                          {value === "deny"
                            ? "Прямой запрет"
                            : "Прямое разрешение"}
                        </span>
                      </small>
                    </span>
                    <button
                      className="button button--text access-list__remove"
                      type="button"
                      disabled={mutation.isPending}
                      onClick={() =>
                        mutation.mutate({
                          operation: () => {
                            const next = { ...(overrides.data ?? {}) };
                            delete next[key];
                            return services.access!.replaceOverrides(
                              user.id,
                              next,
                              { scopeId: activeScopeId },
                            );
                          },
                        })
                      }
                      aria-label={`Удалить исключение ${label.title}`}
                    >
                      Удалить
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : !overrides.isLoading ? (
            <p className="access-empty">
              Исключений нет — доступ определяется только ролями.
            </p>
          ) : null}
          <div className="access-override-form">
            <label>
              <span className="visually-hidden">Право для override</span>
              <Select
                aria-label="Право для override"
                value={permissionKey}
                onChange={(event) => setPermissionKey(event.target.value)}
                disabled={
                  mutation.isPending || overrides.isError || !activeScopeId
                }
              >
                <option value="">Выберите право…</option>
                {permissions.map((permission) => {
                  const label = permissionLabel(permission);
                  return (
                    <option key={permission.key} value={permission.key}>
                      {label.group}: {label.title}
                    </option>
                  );
                })}
              </Select>
            </label>
            <label>
              <span className="visually-hidden">Решение override</span>
              <Select
                aria-label="Решение override"
                value={decision}
                onChange={(event) =>
                  setDecision(event.target.value as Decision)
                }
                disabled={
                  mutation.isPending || overrides.isError || !activeScopeId
                }
              >
                <option value="allow">Разрешить</option>
                <option value="deny">Запретить</option>
              </Select>
            </label>
            <button
              className="button button--secondary"
              type="button"
              disabled={
                !permissionKey ||
                mutation.isPending ||
                overrides.isError ||
                !activeScopeId
              }
              onClick={() =>
                mutation.mutate({
                  operation: async () => {
                    await services.access!.replaceOverrides(
                      user.id,
                      { ...(overrides.data ?? {}), [permissionKey]: decision },
                      { scopeId: activeScopeId },
                    );
                    setPermissionKey("");
                  },
                })
              }
            >
              Добавить
            </button>
          </div>
        </div>
      </div>

      <details className="access-diagnostics">
        <summary>Проверить, почему право разрешено или запрещено</summary>
        <div className="access-diagnostics__content">
          <p>
            Выберите конкретное право, чтобы увидеть итог и источник. Полный
            список не показывается намеренно — он мешает работе в карточке.
          </p>
          <label>
            <span>Право</span>
            <Select
              aria-label="Право для проверки"
              value={checkedPermission}
              onChange={(event) => setCheckedPermission(event.target.value)}
            >
              <option value="">Выберите право…</option>
              {permissions.map((permission) => {
                const label = permissionLabel(permission);
                return (
                  <option key={permission.key} value={permission.key}>
                    {label.group}: {label.title}
                  </option>
                );
              })}
            </Select>
          </label>
          {checkedPermission && selectedPermission ? (
            <div className="access-verdict" role="status">
              <span
                className={
                  selectedAllowed
                    ? "access-state access-state--allow"
                    : "access-state access-state--deny"
                }
              >
                {selectedAllowed ? "Разрешено" : "Запрещено"}
              </span>
              <div>
                <strong>{permissionLabel(selectedPermission).title}</strong>
                <p>{source(selectedPermission.key)}</p>
                <small>
                  <code>{selectedPermission.key}</code> ·{" "}
                  {permissionLabel(selectedPermission).description}
                </small>
              </div>
            </div>
          ) : null}
          {effective.isLoading ? (
            <p className="access-empty">Проверяем действующие права…</p>
          ) : null}
          {effective.isError ? (
            <Notice>
              Не удалось получить результат проверки. Повторите попытку позже.
            </Notice>
          ) : null}
        </div>
      </details>
      {mutation.error ? (
        <Notice>{errorMessage(mutation.error, lastHoldingWide)}</Notice>
      ) : null}
    </section>
  );
}
